// src/features/rafraichissement/VeilleChangements.tsx — SCE Issues
// Monté une fois (App.tsx) : les listes se tiennent à jour toutes seules (03/11).
//
// Pas de temps réel (Static Web App, pas de serveur) : toutes les minutes, tant
// que l'onglet est visible, on lit le DERNIER événement de l'activité — une
// ligne, sur la colonne Date indexée. Création, qualification, commentaire,
// clôture, envoi au BE en écrivent tous un : s'il a changé, quelqu'un a agi, et
// toutes les listes montées se relisent EN SILENCE (signalerProblemesModifies :
// ni squelette ni perte du défilement, comme à la fermeture d'une fiche). La
// cloche se relit au même signal.
//
// Retour sur l'onglet après plus de 2 minutes caché (téléphone en poche, autre
// fenêtre) : relu tout de suite, sans attendre.
//
// Limite connue : une correction de titre, de description, de N° de plan ou
// d'EDE n'écrit pas d'événement depuis le 23/10 — elle se voit au prochain
// changement, ou au bouton Rafraîchir, qui reste.

import { useEffect } from 'react'
import { dernierEvenement } from '@/api/commentaires.api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { signalerProblemesModifies } from '@/hooks/signalProblemesModifies'

const INTERVALLE_MS = 60_000
const ABSENCE_MS = 2 * 60_000

export function VeilleChangements(): null {
  const { user } = useCurrentUser()
  const actif = !!user

  useEffect(() => {
    if (!actif) return
    let dernier: string | null | undefined // undefined : pas encore lu
    let cacheDepuis = document.visibilityState === 'hidden' ? Date.now() : 0
    let enCours = false

    async function verifier(): Promise<void> {
      if (enCours) return
      enCours = true
      try {
        const id = await dernierEvenement()
        if (dernier !== undefined && id !== dernier) signalerProblemesModifies()
        dernier = id
      } catch (err) {
        // Réseau coupé, jeton à renouveler : on réessaiera à la minute suivante.
        console.warn('[veille] dernier événement illisible :', err)
      } finally {
        enCours = false
      }
    }

    void verifier()
    const minuterie = window.setInterval(() => {
      if (document.visibilityState === 'visible') void verifier()
    }, INTERVALLE_MS)

    function surVisibilite(): void {
      if (document.visibilityState === 'hidden') {
        cacheDepuis = Date.now()
        return
      }
      if (cacheDepuis && Date.now() - cacheDepuis >= ABSENCE_MS) {
        // Longtemps ailleurs : tout relire, et repartir de l'événement actuel.
        signalerProblemesModifies()
        dernier = undefined
        void verifier()
      }
      cacheDepuis = 0
    }
    document.addEventListener('visibilitychange', surVisibilite)
    return () => {
      window.clearInterval(minuterie)
      document.removeEventListener('visibilitychange', surVisibilite)
    }
  }, [actif])

  return null
}
